import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { formatDateTime } from '../../services/expenseVouchersApi';
import { GROUP_LABELS, ROUTE_LABELS } from './voucherShared';

const APPROVER_FIELD = { REPORTING: 'reportingApprovers', MANAGEMENT: 'managementApprovers' };

// Routing + per-group approval state of a voucher (read-only). Works with the employee
// voucher DTO and the approval DTO (whose approvers also carry `active`).
//   blockedGroups: groups whose approvers are all inactive (approval DTO only)
const ApprovalProgress = ({ voucher, blockedGroups = [] }) => {
  const groups = voucher.requiredGroups || [];
  const approvals = new Map((voucher.groupApprovals || []).map((g) => [g.group, g]));
  const routing = voucher.routing;

  return (
    <Box>
      {routing?.option && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Routed by {routing.byName || 'Accounts'} on {formatDateTime(routing.at)}: <strong>{ROUTE_LABELS[routing.option] || routing.option}</strong>
          {groups.length > 1 && ' - one approval is needed from each group, in any order.'}
        </Typography>
      )}
      <Stack spacing={1.5}>
        {groups.map((group) => {
          const approval = approvals.get(group);
          const blocked = !approval && blockedGroups.includes(group);
          const approvers = voucher[APPROVER_FIELD[group]] || [];
          return (
            <Box key={group} data-testid={`group-${group}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">{GROUP_LABELS[group]} approval</Typography>
                {approval && <Chip size="small" color="success" label="Approved" />}
                {!approval && !blocked && voucher.status === 'PENDING_APPROVAL' && <Chip size="small" color="warning" label="Pending" />}
                {blocked && <Chip size="small" color="error" label="Blocked: no active approver" />}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: approval ? 0.5 : 0 }}>
                {approvers.map((p) => (
                  <Chip
                    key={String(p.userId)}
                    size="small"
                    variant="outlined"
                    color={p.active === false ? 'error' : 'default'}
                    label={`${p.name || p.email}${p.active === false ? ' (inactive)' : ''}`}
                  />
                ))}
              </Box>
              {approval && (
                <Typography variant="body2" color="text.secondary">
                  Approved by {approval.name} on {formatDateTime(approval.at)}{approval.note ? ` - "${approval.note}"` : ''}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ApprovalProgress;
